import axios from 'axios';
import catchify from 'catchify';
import set from 'lodash/set';

import {
  STRIPE_ELEMENT_SLUG,
  STRIPE_ELEMENTS_PAYMENT_OPTIONS,
} from '@/constants/change-plan.js';
import { getCSRFCookie } from '~/util/global';

const buildApiRequest = (
  url,
  method = 'get',
  data = {},
  axiosInstance = axios,
  headers = { 'Content-Type': 'application/json' },
) => (
  catchify(axiosInstance({
    data,
    url,
    method,
    withCredentials: true,
    headers: {
      'X-CSRFToken': getCSRFCookie(),
      ...headers,
    },
  }))
);

const billingRequestHelper = (data, commit, nuxtAxios) => {
  const {
    country,
    postalCode,
    saveBillingDetails,
  } = data;
  // Use same endpoints (and format the data) as the django checkout
  const billingData = {
    'hidden-card-element-country': country,
    'hidden-tax-zip': postalCode,
    'save-default-billing-info': saveBillingDetails ? 'on' : '',
    'is-license-upgrade': true,
  };

  commit('setBillingDetails', {
    billingCountry: country,
    billingPostalCode: postalCode,
  });

  return buildApiRequest(
    '/api/users/update_billing_info/',
    'post',
    billingData,
    nuxtAxios,
  );
};

const abnRequestHelper = (data, nuxtAxios) => {
  const {
    abn,
    abnBusinessName,
    initialAbn,
    isAbnBusiness,
    saveAbn,
  } = data;

  const abnData = {
    'tax-id': abn,
    'tax-id-initial': initialAbn,
    'tax-id-type': 'au_abn', // Hardcoded for now
    'tax-business-name': abnBusinessName,
    'is-a-business': isAbnBusiness ? 'on' : '',
    'save-default-tax-id': saveAbn ? 'on' : '',
  };

  return buildApiRequest(
    '/api/users/update_tax_id/',
    'post',
    abnData,
    nuxtAxios,
  );
};

const initialState = () => ({
  billingCountry: null,
  billingPostalCode: null,
  error: '',
  invoice: null,
  isComplete: false,
  isLoading: false,
  isReady: false,
  isStripeExperiment: false,
  order: null,
  paymentType: '',
});

export const mutations = {
  resetState(state) {
    const initState = initialState();

    initState.isStripeExperiment = state.isStripeExperiment;
    Object.assign(state, initState);
  },
  setError(state, value) {
    set(state, 'error', value);
  },
  setIsComplete(state, value) {
    set(state, 'isComplete', value);
  },
  setIsLoading(state, value) {
    set(state, 'isLoading', value);
  },
  setIsReady(state, value) {
    set(state, 'isReady', value);
  },
  setIsStripeExperiment(state, value) {
    set(state, 'isStripeExperiment', value);
  },
  setPaymentType(state, value) {
    set(state, 'paymentType', value);
  },
  setOrder(state, value) {
    set(state, 'order', value);
  },
  setBillingDetails(state, value) {
    const { billingCountry, billingPostalCode } = value;

    set(state, 'billingCountry', billingCountry);
    set(state, 'billingPostalCode', billingPostalCode);
  },
  setInvoice(state, value) {
    set(state, 'invoice', value);
  },
};

export const getters = {
  error: (state) => state.error,
  isComplete: (state) => state.isComplete,
  isLoading: (state) => state.isLoading,
  isReady: (state) => state.isReady,
  isStripeExperiment: (state) => state.isStripeExperiment,
  paymentType: (state) => state.paymentType,
  order: (state) => state.order,
  invoice: (state) => state.invoice,
  billingCountry: (state) => state.billingCountry,
  billingPostalCode: (state) => state.billingPostalCode,
  billingDetails: (state, stateGetters) => {
    const { billingCountry, billingPostalCode } = stateGetters;
    const detailsObj = {
      address: {
        country: billingCountry,
      },
    };

    if (billingCountry === 'US') {
      detailsObj.address.postal_code = billingPostalCode;
    }

    return detailsObj;
  },
};

export const actions = {
  onChange({ state, commit }, event = {}) {
    const {
      value,
      complete,
      error,
    } = event;

    if (value.type && value.type !== state.paymentType) {
      commit('setPaymentType', value.type);

      const pmObj = {
        key: 'stripe-elements-payment',
        id: STRIPE_ELEMENT_SLUG,
        brand: value.type,
        paymentType: STRIPE_ELEMENT_SLUG,
        label: STRIPE_ELEMENTS_PAYMENT_OPTIONS[value.type]?.label || 'Stripe payment',
      };

      commit('changePlan/setSelectedPaymentMethodObject', pmObj, { root: true });
    }

    commit('setIsComplete', complete);
    commit('setError', error);
  },

  async fetchStripeExperimentResult({ commit }) {
    const isStripeElementsExp = await this.$experiments.enroll('stripe-elements') === 'new-stripe';
    commit('setIsStripeExperiment', isStripeElementsExp);
  },

  async createOrUpdateOrder({
    commit,
    dispatch,
    state,
    rootGetters,
  }, uuid) {
    // Clear old order and error
    commit('setOrder', null);
    commit('setError', '');

    if (!state.isStripeExperiment) return;

    // tax applies only for upgrade
    const isDowngrade = rootGetters['changePlan/isDowngrading'];
    if (isDowngrade) return;

    // getting new plan
    const newPlanSelectedId = rootGetters['changePlan/newPlanSelected']?.id;
    if (!newPlanSelectedId || !uuid) return;

    commit('setIsLoading', true);

    const data = {
      uuid,
      plan_id: newPlanSelectedId,
      stripe_order_id: state.order?.stripe_order_id,
    };
    const [error, response] = await buildApiRequest(
      '/cart/stripe-elements/order/',
      'post',
      data,
      this.$axios,
    );

    commit('setIsLoading', false);

    if (error) {
      commit('setError', error?.response?.data);

      if (error?.response?.status !== 400) {
        commit('setIsStripeExperiment', false);
        // Need to re-fetch cards if disabling stripe-elements
        await dispatch('changePlan/fetchUserCards', {}, { root: true });
      }
    } else {
      commit('changePlan/setIsBillingDetailsValid', true, { root: true });
      commit('changePlan/setCurrentStepKey', 'select-payment', { root: true });
      commit('setOrder', response.data);
    }
  },

  async reopenOrder({
    commit,
    dispatch,
    state,
    rootGetters,
  }, uuid) {
    // validate stripe-elements flag
    if (!state.isStripeExperiment) return;

    const orderId = state.order?.stripe_order_id;
    const newPlanSelectedId = rootGetters['changePlan/newPlanSelected']?.id;
    let errorMsg = '';

    if (!orderId) {
      errorMsg = 'Missing order ID';
    } else if (!uuid) {
      errorMsg = 'Missing uuid';
    } else if (!newPlanSelectedId) {
      errorMsg = 'Cannot get new plan';
    }

    if (errorMsg) {
      commit('setError', errorMsg);
      return;
    }

    commit('setIsLoading', true);

    const data = {
      action: 'reopen_licensing_order',
      plan_id: newPlanSelectedId,
      stripe_order_id: orderId,
      uuid,
    };
    const [error, response] = await buildApiRequest(
      '/cart/stripe-elements/order/',
      'put',
      data,
      this.$axios,
    );

    commit('setIsLoading', false);

    if (error) {
      commit('setError', error);
      commit('setOrder', null);
      commit('setIsStripeExperiment', false);
      // Need to re-fetch cards if disabling stripe-elements
      await dispatch('changePlan/fetchUserCards', {}, { root: true });
    } else {
      commit('setOrder', response.data);
    }
  },

  async updateBillingDetails({ commit, dispatch, rootGetters }, data) {
    const currentPlanUUID = rootGetters['changePlan/currentPlan']?.uuid;
    commit('setIsLoading', true);

    const [respError] = await billingRequestHelper(data, commit, this.$axios);

    if (respError) {
      // TODO -- Handle errors
      commit('setError', 'Some error');
      return;
    }

    await dispatch('createOrUpdateOrder', currentPlanUUID);

    commit('setIsLoading', false);
  },

  async updateAbnDetails({ commit, dispatch, rootGetters }, data) {
    const currentPlanUUID = rootGetters['changePlan/currentPlan']?.uuid;
    commit('setIsLoading', true);

    const [respError] = await abnRequestHelper(data, this.$axios);

    if (respError) {
      // TODO -- Handle errors
      commit('setError', 'Some error');
      return;
    }

    await dispatch('createOrUpdateOrder', currentPlanUUID);

    commit('setIsLoading', false);
  },

  async submitBillingDetails(context, data) {
    const {
      commit,
      dispatch,
      rootGetters,
    } = context;

    commit('setIsLoading', true);

    const [
      [billingErr],
      [abnError],
    ] = await Promise.all([
      billingRequestHelper(data, commit, this.$axios),
      abnRequestHelper(data),
    ]);

    // TODO -- Handle errors
    if (billingErr || abnError) {
      commit('setError', 'Some error');
      commit('setIsLoading', false);
      return;
    }

    const currentPlanUUID = rootGetters['changePlan/currentPlan']?.uuid;

    await Promise.all([
      dispatch('changePlan/fetchUserCards', {}, { root: true }),
      dispatch('createOrUpdateOrder', currentPlanUUID),
    ]);

    commit('changePlan/setIsABNValid', true, { root: true });
    commit('setIsLoading', false);
  },

  async queryInvoice({ commit }, orderId) {
    if (!orderId) return;

    const [, invoiceData] = await buildApiRequest(
      'cart/stripe-elements/order/status/',
      'post',
      { order_id: orderId },
      this.$axios,
    );

    if (!invoiceData?.data) return;

    if (Object.keys(invoiceData.data).length !== 0) {
      commit('setInvoice', invoiceData.data);
    }
  },

  resetUpgradeState({ commit }) {
    commit('resetState');
    commit('changePlan/setIsBillingDetailsValid', false, { root: true });
    commit('changePlan/setIsABNValid', false, { root: true });
    commit('changePlan/setNewPlanSelected', null, { root: true });
    commit('changePlan/setCurrentStepKey', 'select-tier', { root: true });
  },
};

export { initialState as state };
